import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupHeading } from 'utils/groupLabel';
import { sortByEventGroupGenderName } from 'utils/athleteOrder';
import style from './AthletePRsTable.module.css';
import { convertDateTime, convertMillisecondsToRecord } from 'utils/time';

/**
 * Determine if an event is a time-based event based on category
 * Track events use time (lower is better)
 * Field events use distance/height (higher is better)
 */
const isTimeEvent = (eventCategory) => {
  return eventCategory === 'Track';
};

/**
 * Format season best value based on event category
 * Time events: Format as time (MM:SS.ms)
 * Field events: Format as distance/height (meters with 2 decimals)
 */
const formatSeasonBest = (value, eventCategory) => {
  if (!value) return '';
  
  if (isTimeEvent(eventCategory)) {
    return convertMillisecondsToRecord(parseFloat(value), 2);
  } else {
    return parseFloat(value).toFixed(2);
  }
};

// Each row carries its own event (eventTypeName), its place in the club's order
// (eventOrder) and its category (eventCategory), as the table can list several
// events at once.
const AthletePRsTable = ({
  athletePoints,
  searchText,
  setSearchText,
}) => {
  const [athletePointsTableData, setAthletePointsTableData] = useState([]);
  // The athlete's group column reads Age and "U11", or Year and "Y6" for a club that groups by year level.
  const { groupingBasis, words } = useGroupLabels();

  const columns = [
    { key: 'athleteClubID', label: 'Club ID' },
    { key: 'athleteName', label: 'Athlete Name' },
    { key: 'ageGroup', label: words.value },
    { key: 'gender', label: 'Gender' },
    { key: 'eventTypeName', label: 'Event' },
    { key: 'dateAchieved', label: 'Date' },
    { key: 'seasonBest', label: 'Result' },
  ];

  const [sortKeys, setSortKeys] = useState(
    columns.reduce((acc, col) => {
      acc[col.key] = false;
      return acc;
    }, {})
  );

  useEffect(() => {
    // Listed by event in the club's order, then youngest group first, then female before male, then
    // last name: the order the report's files use too (utils/athleteOrder.js). A click on a column
    // heading re-sorts from there.
    const sortedByGroup = sortByEventGroupGenderName(athletePoints, {
      event: (point) => point.eventOrder,
      group: (point) => point.age,
      gender: (point) => point.gender,
      lastName: (point) => point.lastName,
      firstName: (point) => point.firstName,
    })
      .map((point) => {
        return {
          ...point,
          // Keep raw numeric value for sorting
          seasonBestRaw: point.seasonBest,
          // Format for display, by the row's own event
          seasonBest: formatSeasonBest(point.seasonBest, point.eventCategory),
          // The date as the server sends it (2026-09-12T00:00:00), for sorting
          dateAchievedRaw: point.dateAchieved,
          dateAchieved: convertDateTime(point.dateAchieved).date,
          ageGroup: formatGroupHeading(groupingBasis, point.age),
          gender: point.gender === 'M' ? 'Male' : 'Female',
        };
      });

    setAthletePointsTableData(sortedByGroup);
  }, [athletePoints, groupingBasis]);

  const handleAthleteSearch = (e) => {
    setSearchText(e.target.value);
  };

  // A click on a heading sorts by that column within each event, the events kept in the club's order,
  // as the Result heading does (Doug, 2026-10-25); the Event heading sorts the events themselves. The
  // next click on the same heading reverses it. Each column sorts by its value, not the text shown: the
  // Age column by the group number (U6 before U10, Y6 before Y10), the Date column by the date.
  const compareColumn = (key, a, b) => {
    switch (key) {
      case 'athleteName':
      case 'gender':
        return a[key].toLowerCase().localeCompare(b[key].toLowerCase());
      case 'ageGroup':
        return a.age - b.age;
      case 'dateAchieved':
        return String(a.dateAchievedRaw).localeCompare(String(b.dateAchievedRaw));
      case 'seasonBest':
        // Best first: a time lowest first, a distance or height highest first
        return isTimeEvent(a.eventCategory)
          ? a.seasonBestRaw - b.seasonBestRaw
          : b.seasonBestRaw - a.seasonBestRaw;
      default:
        // The club ID
        return a[key] - b[key];
    }
  };

  const sortTable = (key) => {
    const direction = sortKeys[key] ? -1 : 1;
    // Create a copy to avoid mutating state directly
    const sortData = [...athletePointsTableData].sort((a, b) => {
      if (key === 'eventTypeName') {
        return direction * (a.eventOrder - b.eventOrder);
      }
      return a.eventOrder - b.eventOrder || direction * compareColumn(key, a, b);
    });
    
    setAthletePointsTableData(sortData);
    
    // Toggle sort direction
    setSortKeys({
      ...sortKeys,
      [key]: !sortKeys[key],
    });
  };

  return (
    <div className={style.tableCard}>
      {/* Search Bar */}
      <div className={style.searchBar}>
        <input
          type="text"
          className={style.searchInput}
          placeholder="Search athletes by name..."
          id="athleteSearch"
          // Held by the report, so the box still shows the search after the table reloads.
          value={searchText}
          onChange={handleAthleteSearch}
        />
      </div>

      {/* Table */}
      <div className={style.tableWrapper}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th key={index} onClick={() => sortTable(col.key)}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody id="athleteTableBody">
            {athletePointsTableData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6c757d',
                  }}
                >
                  No athletes found for the selected filters
                </td>
              </tr>
            ) : (
              athletePointsTableData.map((point, index) => (
                <tr key={index}>
                  <td className={style.rankCell}>{point.athleteClubID}</td>
                  <td className={style.athleteName}>{point.athleteName}</td>
                  <td>
                    <span className={style.ageBadge}>{point.ageGroup}</span>
                  </td>
                  <td>
                    <span
                      className={`${style.genderBadge} ${style[point.gender.toLowerCase()]}`}
                    >
                      {point.gender}
                    </span>
                  </td>
                  <td>{point.eventTypeName}</td>
                  <td>{point.dateAchieved}</td>
                  <td className={style.pointsCell}>{point.seasonBest}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

AthletePRsTable.propTypes = {
  athletePoints: PropTypes.array,
  searchText: PropTypes.string,
  setSearchText: PropTypes.func,
};

export default AthletePRsTable;